import React, { useCallback, useEffect, useState } from "react";
import EventDetailsCard from "../components/event-details-card";
import EventDetailsModal from "../components/event-details-modal";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import { DateRangePicker } from "react-dates";
import moment from "moment";
import {
  FaCopy,
  FaSearch,
  FaTh,
  FaList,
  FaChevronLeft,
  FaChevronRight,
  FaEye,
  FaEdit,
  FaUserPlus,
  FaKey,
  FaBell,
  FaDownload,
  FaTrash,
  FaCreditCard,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaMapSigns,
  FaUser,
  FaPhoneAlt,
  FaLock,
} from "react-icons/fa";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setActivePage, setShowPreloader } from "../store/activePage";
import HandleGetEventList from "../hooks/handleGetEventList";
import getEventCreds from "../hooks/getEventCreds";
import CustomModal from "../components/modal";
import HandleEditEventInfo from "../hooks/handleUpdateEvent";
import HandleDeleteEvent from "../hooks/handleDeleteEvent";
import Swal from "sweetalert2";
import handleDownloadOverallReport from "../hooks/handleDownloadOverallReport";
import handleCloseEvent from "../hooks/handleCloseEvent";
import HandleEventOperatorModal from "../components/event-handle-operator-modal";
import HandleAddOperator from "../hooks/addOperator";
import { ErrorToast, SuccessToast } from "../components/toastify";
import { MdDelete } from "react-icons/md";
import handleDeleteOperator from "../hooks/handleDeleteOperator";
import handleCopy from "../helpers/copyToClipboard";
import EventAddServiceModal from "../components/event-add-service-modal";
import HandleAddEventService from "../hooks/addEventServices";
import HandleCancelCallService from "../hooks/cancelCallService";
import HandleCancelSmsService from "../hooks/cancelSmsService";
import HandleCancelConfirmationSmsService from "../hooks/cancelConfirmationSms";
import convertoformdata from "../helpers/convertToFormdata";
import { AxiosPostMethod } from "../helpers/customAxios";
import handleUpdateEventPayment from "../hooks/handleUpdatePayment";
import PaymentComponent from "../components/payment-gateway";
import HandleLogout from "../hooks/handleLogout";
import formatCustomDateWithTime from "../helpers/customDateFormatWithTime";
import useWalletHealthCheck from "../hooks/useWalletHealthCheck";
import WalletLowBalanceAlert from "../components/wallet-low-balance-alert";

// ─── List-view helpers ────────────────────────────────────────────────────────
const listBtnColors = {
  blue: "bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-200",
  indigo: "bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border-indigo-200",
  purple: "bg-purple-50 text-purple-600 hover:bg-purple-100 border-purple-200",
  emerald:
    "bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border-emerald-200",
  cyan: "bg-cyan-50 text-cyan-600 hover:bg-cyan-100 border-cyan-200",
  green: "bg-green-50 text-green-600 hover:bg-green-100 border-green-200",
  orange: "bg-orange-50 text-orange-600 hover:bg-orange-100 border-orange-200",
  red: "bg-red-50 text-red-600 hover:bg-red-100 border-red-200",
};

const getCreatedAtValue = (event) => {
  const rawValue =
    event?.created_at ||
    event?.registered_on ||
    event?.createdAt ||
    event?.registeredOn ||
    null;
  const timestamp = rawValue ? new Date(rawValue).getTime() : 0;
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

function ListActionBtn({ color = "blue", title, onClick, children }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`w-7 h-7 rounded-lg border flex items-center justify-center text-xs transition-colors ${listBtnColors[color]}`}
    >
      {children}
    </button>
  );
}

function ServicePill({ active, label }) {
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${
        Number(active) > 0
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-gray-100 text-gray-400 border-gray-200"
      }`}
    >
      {Number(active) > 0 ? "✓" : "✗"} {label}
    </span>
  );
}

// ─── Credential Card ─────────────────────────────────────────────────────────
function CredentialCard({ items, onDelete, onCopyAll }) {
  const initials = (items.operator_name || "OP")
    .split(" ")
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const copyField = async (label, value) => {
    const ok = await handleCopy(value);
    if (ok) SuccessToast(`${label} copied`);
  };

  return (
    <div className="rounded-2xl overflow-hidden border border-gray-100 bg-white shadow-xl shadow-slate-200/60 mb-3 text-left">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-orange-500 px-5 py-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-white/20 border border-white/30 flex items-center justify-center text-white font-black text-sm shrink-0 select-none">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold text-sm leading-tight truncate">
            {items.operator_name}
          </p>
          <p className="text-orange-100/90 text-xs mt-0.5">{items.operator_mobile}</p>
        </div>
        <span className="text-[10px] font-bold px-2 py-0.5 bg-orange-700/40 text-orange-100 border border-orange-400/50 rounded-full uppercase tracking-widest shrink-0">
          Operator
        </span>
      </div>

      {/* Credential fields */}
      <div className="bg-gray-50 px-5 py-4 space-y-3">
        {[
          { label: "Username", value: items.username },
          { label: "Password", value: items.password },
        ].map(({ label, value }) => (
          <div key={label}>
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">
              {label}
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm font-mono font-semibold text-gray-900 bg-white border border-gray-200 rounded-lg px-3 py-2 min-w-0 truncate">
                {value}
              </code>
              <button
                onClick={() => copyField(label, value)}
                className="shrink-0 w-8 h-8 rounded-lg bg-white hover:bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-500 hover:text-orange-600 transition"
                title={`Copy ${label}`}
              >
                <FaCopy size={11} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Footer actions */}
      <div className="bg-white border-t border-gray-200 px-5 py-3 flex items-center justify-between gap-3">
        {onDelete ? (
          <button
            onClick={onDelete}
            className="flex items-center gap-1.5 text-xs font-semibold text-red-500 hover:text-red-600 transition"
          >
            <MdDelete size={14} /> Remove Operator
          </button>
        ) : (
          <div />
        )}
        <button
          onClick={onCopyAll}
          className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition"
        >
          <FaCopy size={11} /> Copy All Credentials
        </button>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const OperatorAllOpenEvents = () => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);
  const [searchValue, setSearchValue] = useState(null);
  const [eventCreds, setEventCreds] = useState([]);
  const [selectedOperatorTotals, setSelectedOperatorTotals] = useState(null);
  const [addOperatorEventId, setAddOperatorEventId] = useState(null);
  const [eventDetails, setEventDetails] = useState([]);
  const [showAddService, setShowAddService] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isEditable, setIsEditable] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [showFilters, setShowFilters] = useState(false);
  const [showPaymentPendingOnly, setShowPaymentPendingOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0,
  });
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

  const dispatch = useDispatch();
  const { healthData: walletHealth, loading: walletHealthLoading } = useWalletHealthCheck();

  useEffect(() => {
    dispatch(
      setActivePage({
        headerName: "All Open Events",
        activeMenu: "upcomingEvents",
      }),
    );
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchValue((searchValue || "").trim());
      setPage(1);
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, showPaymentPendingOnly]);

  const getEventList = useCallback(async () => {
    dispatch(setShowPreloader(true));
    const eventList = await HandleGetEventList(
      startDate?.format("YYYY-MM-DD") || null,
      endDate?.format("YYYY-MM-DD") || null,
      debouncedSearchValue,
      "open",
      showPaymentPendingOnly,
      { page, perPage: 10 },
    );
    dispatch(setShowPreloader(false));
    if (eventList && Array.isArray(eventList.items)) {
      const sortedEvents = [...eventList.items].sort(
        (a, b) => getCreatedAtValue(b) - getCreatedAtValue(a),
      );
      setEventDetails(sortedEvents);
      setPagination(
        eventList.pagination || {
          page,
          per_page: 10,
          total: 0,
          total_pages: 0,
        },
      );
    } else {
      setEventDetails([]);
      setPagination({
        page: 1,
        per_page: 10,
        total: 0,
        total_pages: 0,
      });
    }
  }, [startDate, endDate, debouncedSearchValue, showPaymentPendingOnly, dispatch, page]);

  useEffect(() => {
    getEventList();
  }, [getEventList]);

  const handleEdit = (event) => {
    setSelectedEvent(event);
    setIsEditable(true);
  };

  const HandleSuccess = async (razval, event_id) => {
    SuccessToast("Payment successfull");
    await handleUpdateEventPayment(
      { ...razval, ...{ event_id, cost: process.env.REACT_APP_EVENT_PRICING } },
      dispatch,
    );
    getEventList();
  };

  const HandleFailure = () => {
    ErrorToast("Error faced on payment, Please try again later");
  };

  const triggerPayment = async (event_id) => {
    dispatch(setShowPreloader(true));
    const orderDetails = await AxiosPostMethod("org/event/order-create", {
      amount: process.env.REACT_APP_EVENT_PRICING,
    });
    dispatch(setShowPreloader(false));
    if (orderDetails.status === 200) {
      PaymentComponent(
        orderDetails.data.data,
        (razval) => HandleSuccess(razval, event_id),
        HandleFailure,
      );
    } else if (orderDetails.status === 401) HandleLogout();
    else ErrorToast("Error on Payment creation");
  };

  const HandleShowEventCreds = async (id) => {
    const creds = await getEventCreds(id);
    if (creds) setEventCreds(creds);
  };

  const handleSave = async (updatedEvent) => {
    dispatch(setShowPreloader(true));
    const updateEvent = await HandleEditEventInfo(updatedEvent);
    dispatch(setShowPreloader(false));
    if (updateEvent) getEventList();
  };

  const deleteEventHandler = async (event_id) => {
    dispatch(setShowPreloader(true));
    const DeleteRes = await HandleDeleteEvent(event_id);
    dispatch(setShowPreloader(false));
    if (DeleteRes) getEventList();
  };

  const handleCloseModal = () => {
    setSelectedEvent(null);
    setIsEditable(false);
  };

  const handlePreset = (type) => {
    const today = moment();
    let newStartDate = today.clone();
    let newEndDate = today.clone();

    switch (type) {
      case "1 Week":
        newEndDate = today.clone().add(6, "days");
        break;
      case "1 Month":
        newEndDate = today.clone().add(1, "months");
        break;
      case "3 Months":
        newEndDate = today.clone().add(3, "months");
        break;
      default:
        newStartDate = null;
        newEndDate = null;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleDeleteOperatorLocal = async (operator_id) => {
    const deleteRes = await handleDeleteOperator(operator_id, dispatch);
    if (deleteRes) setEventCreds([]);
  };

  const handleDeleteVoiceCall = async (event_id) => {
    const confirmed = await Swal.fire({
      title: "Remove Voice Call Service?",
      text: "This will cancel the voice call reminder for this event.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Remove",
      confirmButtonColor: "#dc2626",
    });
    if (!confirmed.isConfirmed) return;
    dispatch(setShowPreloader(true));
    const res = await HandleCancelCallService({ event_id });
    dispatch(setShowPreloader(false));
    if (res) {
      SuccessToast("Voice call service removed");
      getEventList();
    }
  };

  const handleDeleteSmsService = async (event_id) => {
    const confirmed = await Swal.fire({
      title: "Remove SMS Service?",
      text: "This will cancel the SMS reminder for this event.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Remove",
      confirmButtonColor: "#dc2626",
    });
    if (!confirmed.isConfirmed) return;
    dispatch(setShowPreloader(true));
    const res = await HandleCancelSmsService({ event_id });
    dispatch(setShowPreloader(false));
    if (res) {
      SuccessToast("SMS service removed");
      getEventList();
    }
  };

  const handleDeleteConfirmationSms = async (event_id) => {
    const confirmed = await Swal.fire({
      title: "Remove Confirmation SMS?",
      text: "This will cancel the confirmation SMS for this event.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Remove",
      confirmButtonColor: "#dc2626",
    });
    if (!confirmed.isConfirmed) return;
    dispatch(setShowPreloader(true));
    const res = await HandleCancelConfirmationSmsService({ event_id });
    dispatch(setShowPreloader(false));
    if (res) {
      SuccessToast("Confirmation SMS service removed");
      getEventList();
    }
  };

  const handleAddservice = async (values, resetForm) => {
    dispatch(setShowPreloader(true));
    
    console.log("=== EVENT SERVICE SUBMISSION DEBUG LOG ===");
    console.log("Form Values Received:", values);
    console.log("Custom List Locations:", values.custom_list_locations);
    console.log("Custom List File:", values.custom_list_file?.name);
    console.log("Voice Call Credits:", values.voice_call_credits);
    console.log("showAddService state:", showAddService);
    
    // Build the payload carefully based on which services are already enabled vs being added
    const validatedData = {
      // Voice: If already enabled, keep existing values. Otherwise use form values.
      reminder_voice:
        Number(showAddService.voice) === 1 ? 1 : values.reminder_voice,
      reminder_call_date:
        Number(showAddService.voice) === 1
          ? showAddService.reminder_call_date
          : values.reminder_call_date,
      reminder_voice_id:
        Number(showAddService.voice) === 1
          ? showAddService.reminder_voice_id
          : values.reminder_voice_id,
      voice_call_credits: values.voice_call_credits || 0,
      
      // SMS: If already enabled, keep existing values. Otherwise use form values.
      reminder_sms: Number(showAddService.sms) === 1 ? 1 : values.reminder_sms,
      reminder_sms_date:
        Number(showAddService.sms) === 1
          ? showAddService.reminder_sms_date
          : values.reminder_sms_date,
      
      // MOI Confirmation SMS
      moi_confirmation_sms:
        Number(showAddService.confirmSms) === 1
          ? 1
          : values.moi_confirmation_sms,
      
      // Custom list
      has_custom_list: values.has_custom_list,
      custom_list_locations: values.custom_list_locations || [],
      custom_list_file: values.custom_list_file,
      
      // Event identifiers
      event_id: showAddService.event_id,
      customer_id: showAddService.customer_id,
      event_date: showAddService.event_date,
    };

    console.log("Validated Data to Send:", validatedData);
    console.log("=== SENDING TO API: org/event/add-service ===");

    const convertedDate = convertoformdata(validatedData);
    const addedSer = await HandleAddEventService(convertedDate);
    dispatch(setShowPreloader(false));
    
    if (addedSer) {
      SuccessToast("Services added successfully");
      console.log("✅ API SUCCESS: Services added to event_automation_list");
      console.log("Event ID:", showAddService.event_id);
      getEventList();
      resetForm();
      setShowAddService(false);
    } else {
      console.error("❌ API FAILED: Could not add services");
      // Keep modal open on failure so user can retry
    }
  };

return (
    <div className="p-8">
      <WalletLowBalanceAlert healthData={walletHealth} loading={walletHealthLoading} />
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/70 bg-white/90 p-4 shadow-xl shadow-slate-200/60 backdrop-blur">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-teal-700">
            Event Filters
          </p>
          <p className="text-xs text-gray-500">
            Search and date range controls
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() =>
              setShowPaymentPendingOnly((prev) => !prev)
            }
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
              showPaymentPendingOnly
                ? "border border-amber-300 bg-amber-500 text-white shadow"
                : "border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
            }`}
          >
            {showPaymentPendingOnly
              ? "Show All Open Events"
              : "Show Only Pending Payments"}
          </button>
          <div className="flex gap-2 rounded-lg border border-gray-200 bg-gray-50 p-1 shadow-sm">
            <button
              className={`rounded-md p-2 transition ${
                viewMode === "grid"
                  ? "bg-teal-700 text-white shadow"
                  : "text-gray-600 hover:bg-teal-50"
              }`}
              onClick={() => setViewMode("grid")}
              title="Grid View"
            >
              <FaTh />
            </button>
            <button
              className={`rounded-md p-2 transition ${
                viewMode === "list"
                  ? "bg-teal-700 text-white shadow"
                  : "text-gray-600 hover:bg-teal-50"
              }`}
              onClick={() => setViewMode("list")}
              title="List View"
            >
              <FaList />
            </button>
          </div>
          <button
            type="button"
            onClick={() => setShowFilters((prev) => !prev)}
            className="rounded-lg border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-700 transition hover:bg-teal-100"
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-6 rounded-xl border border-white/70 bg-white/90 p-6 shadow-xl shadow-slate-200/60 backdrop-blur">
          {/* Date Picker Section */}
          <div className="w-full md:w-1/2">
            <p className="mb-3 text-lg font-semibold text-gray-900">
              Select Date
            </p>
            <DateRangePicker
              startDate={startDate}
              startDateId="start_date"
              endDate={endDate}
              endDateId="end_date"
              onDatesChange={({ startDate, endDate }) => {
                setStartDate(startDate);
                setEndDate(endDate);
              }}
              focusedInput={focusedInput}
              onFocusChange={(focusedInput) => setFocusedInput(focusedInput)}
              displayFormat="MMM DD, YYYY"
            />

            <div className="mt-4 flex flex-wrap gap-3">
              {[
                { label: "1 Week", color: "orange" },
                { label: "1 Month", color: "orange" },
                { label: "3 Months", color: "orange" },
              ].map(({ label }) => (
                <button
                  key={label}
                  className="rounded-lg bg-orange-50 px-4 py-2 text-sm font-medium text-orange-600 transition hover:bg-orange-100"
                  onClick={() => handlePreset(label)}
                >
                  {label}
                </button>
              ))}
              <button
                className="rounded-lg bg-red-50 px-4 py-2 text-sm font-medium text-red-600 transition hover:bg-red-100"
                onClick={() => handlePreset("Reset")}
              >
                Reset
              </button>
            </div>
          </div>

          <div className="flex w-full items-center gap-4 md:w-1/2">
            <div className="flex flex-1 items-center overflow-hidden rounded-lg border border-gray-200 shadow-sm">
              <input
                type="text"
                placeholder="Search here..."
                className="h-11 w-full px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-400"
                onInput={(event) => setSearchValue(event.target.value)}
              />
              <button className="h-11 bg-gray-50 px-4 transition hover:bg-gray-100">
                <FaSearch className="text-gray-500" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Render based on view mode */}
      {eventDetails?.length > 0 ? (
        viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {eventDetails.map((event, index) => (
              <EventDetailsCard
                onOperatorTotalsShow={(event) => setSelectedOperatorTotals(event.operator_totals)}
                key={index}
                eventDetails={event}
                onClick={(details) => {
                  setSelectedEvent(details);
                  setIsEditable(false);
                }}
                onManageVillages={(eventId) => navigate("/dashboard/villages", { state: { event_id: eventId } })}
                onManageDuplicates={!event.result_file ? (eventId) => navigate(`/dashboard/duplicate-entries?event_id=${eventId}`) : null}
                onEdit={!event.result_file ? handleEdit : null}
                cardStyle={"bg-gray-300"}
                onDelete={
                  !event.result_file
                    ? (event) =>
                        Swal.fire({
                          title: "Are you sure you want to delete this event?",
                          showCancelButton: true,
                          confirmButtonText: "Confirm Deletion",
                          confirmButtonColor: "red",
                        }).then(async (result) => {
                          if (result.isConfirmed)
                            deleteEventHandler(event.event_id);
                        })
                    : null
                }
                onCredShow={
                  !event.result_file &&
                  event?.operators?.[0]?.["operator_name"] !== null
                    ? (id) => HandleShowEventCreds(id)
                    : null
                }
                onAddOperator={
                  !event.result_file ? (id) => setAddOperatorEventId(id) : null
                }
                onDownloadReport={(event_id) =>
                  Swal.fire({
                    title: "Download Print Report",
                    text: "Do you want to download the overall print report?",
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonText: "Download",
                    confirmButtonColor: "#0d9488",
                    cancelButtonText: "Cancel",
                    preConfirm: () => handleDownloadOverallReport(event_id, dispatch),
                    showLoaderOnConfirm: true,
                  })
                }
                onCloseEvent={(event_id) =>
                  Swal.fire({
                    title: "Close Event",
                    text: "Closing this event will move it to Closed Events. Do you want to continue?",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "Yes, Close Event",
                    confirmButtonColor: "#dc2626",
                    cancelButtonText: "Cancel",
                  }).then(async (result) => {
                    if (result.isConfirmed) {
                      const success = await handleCloseEvent(event_id, dispatch);
                      if (success) getEventList();
                    }
                  })
                }
                errorinfo={
                  event?.operators?.[0]?.["operator_name"] !== null
                    ? null
                    : "Add Operators"
                }
                reminderSms={event?.reminder_sms}
                reminderCall={event?.reminder_voice}
                confirmationSms={event?.moi_confirmation_sms}
                addServices={
                  !event.reminder_sms ||
                  !event.reminder_voice ||
                  !event.moi_confirmation_sms
                    ? () =>
                        setShowAddService({
                          voice: event.reminder_voice,
                          sms: event.reminder_sms,
                          confirmSms: event.moi_confirmation_sms,
                          reminder_call_date: event.reminder_call_date,
                          reminder_voice_id: event.reminder_voice_id,
                          reminder_sms_date: event.reminder_sms_date,
                          event_id: event.event_id,
                          customer_id: event.customer_id,
                          event_date: event.event_date,
                        })
                    : null
                }
                tokenCount={event.token_count}
                makePayment={(id) => triggerPayment(id)}
                total_reminder_list={event.total_reminder_list}
                old_entry={event.old_entry}
                onDeleteVoiceCall={
                  Number(event.reminder_voice) > 0
                    ? () => handleDeleteVoiceCall(event.event_id)
                    : null
                }
                onDeleteSms={
                  Number(event.reminder_sms) > 0
                    ? () => handleDeleteSmsService(event.event_id)
                    : null
                }
                onDeleteConfirmationSms={
                  Number(event.moi_confirmation_sms) > 0
                    ? () => handleDeleteConfirmationSms(event.event_id)
                    : null
                }
              />
            ))}
          </div>
        ) : (
          /* ── List View: one card per event ───────────────────────────── */
          <div className="space-y-3">
            {eventDetails.map((event) => {
              const noOperator =
                event?.operators?.[0]?.operator_name === null ||
                !event?.operators?.[0]?.operator_name;
              const hasAllServices =
                event.reminder_sms &&
                event.reminder_voice &&
                event.moi_confirmation_sms;
              const isPast = !!event.old_entry;
              const isPaid = !!event.token_count;
              const isLocked = !!event.result_file;
              const createdOn = event.created_at || event.registered_on;
              const location =
                [event.event_address, event.event_city, event.event_state]
                  .filter(Boolean)
                  .join(", ") || "—";

              return (
                <div
                  key={event.event_id}
                  className="bg-white/90 rounded-xl border border-white/70 shadow-xl shadow-slate-200/60 hover:shadow-lg transition-shadow overflow-hidden backdrop-blur"
                >
                  <div className="flex">
                    {/* Accent strip */}
                    <div
                      className={`w-1.5 shrink-0 ${isPast ? "bg-amber-400" : "bg-emerald-500"}`}
                    />

                    {/* Card body */}
                    <div className="flex-1 px-4 py-3 min-w-0">
                      {/* ── Row 1: name + status badges + action buttons ── */}
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        {/* Name + badges */}
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          <button
                            className="text-sm font-bold text-gray-900 hover:text-blue-600 transition text-left truncate max-w-xs"
                            onClick={() => {
                              setSelectedEvent(event);
                              setIsEditable(false);
                            }}
                          >
                            {event.event_name || event.event_type || "—"}
                          </button>
                          <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full border border-slate-200 tracking-wide shrink-0">
                            Event ID #{event.event_id}
                          </span>
                          {isPast && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full border border-amber-200 uppercase tracking-wide shrink-0">
                              ⏮ Past
                            </span>
                          )}
                          {!isPaid && !isLocked && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 bg-red-100 text-red-600 rounded-full border border-red-200 uppercase tracking-wide shrink-0">
                              Unpaid
                            </span>
                          )}
                          {noOperator && !isLocked && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 bg-orange-100 text-orange-600 rounded-full border border-orange-200 uppercase tracking-wide shrink-0">
                              No Operators
                            </span>
                          )}
                          {isLocked && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full border border-gray-300 uppercase tracking-wide shrink-0">
                              Closed
                            </span>
                          )}
                        </div>

                        {/* Action icon buttons */}
                        <div className="flex items-center gap-1 shrink-0 flex-wrap">
                          {/* View */}
                          <ListActionBtn
                            color="blue"
                            title="View Details"
                            onClick={() => {
                              setSelectedEvent(event);
                              setIsEditable(false);
                            }}
                          >
                            <FaEye />
                          </ListActionBtn>

                          {/* Edit */}
                          {!isLocked && (
                            <ListActionBtn
                              color="indigo"
                              title="Edit Event"
                              onClick={() => handleEdit(event)}
                            >
                              <FaEdit />
                            </ListActionBtn>
                          )}

                          {/* Operator Totals */}
                          {event.operator_totals && event.operator_totals.length > 0 && (
                            <ListActionBtn
                              color="purple"
                              title="View Operator Totals"
                              onClick={() => setSelectedOperatorTotals(event.operator_totals)}
                            >
                              <FaList />
                            </ListActionBtn>
                          )}

                          {!isLocked && (
                            <button type="button" onClick={() => navigate(`/dashboard/duplicate-entries?event_id=${event.event_id}`)}
                              title="Manage Duplicate Entries"
                              className="rounded-lg border border-teal-200 bg-teal-50 px-2 py-2 text-xs font-bold text-teal-800 hover:bg-teal-100 focus:outline-none focus:ring-2 focus:ring-teal-600">
                              Manage Duplicate Entries
                            </button>
                          )}

                          {/* Manage Villages */}
                          {!isLocked && (
                            <ListActionBtn
                              color="indigo"
                              title="Manage Villages"
                              onClick={() =>
                                navigate("/dashboard/villages", { state: { event_id: event.event_id } })
                              }
                            >
                              <FaMapSigns />
                            </ListActionBtn>
                          )}

                          {/* Add Operator */}
                          {!isLocked && (
                            <ListActionBtn
                              color="purple"
                              title="Add Operator "
                              onClick={() =>
                                setAddOperatorEventId(event.event_id)
                              }
                            >
                              <FaUserPlus />
                            </ListActionBtn>
                          )}

                          {/* View Credentials */}
                          {!isLocked && !noOperator && (
                            <ListActionBtn
                              color="emerald"
                              title="View Credentials"
                              onClick={() =>
                                HandleShowEventCreds(event.event_id)
                              }
                            >
                              <FaKey />
                            </ListActionBtn>
                          )}

                          {/* Add Services */}
                          {!hasAllServices && !isLocked && (
                            <ListActionBtn
                              color="cyan"
                              title="Add Services"
                              onClick={() =>
                                setShowAddService({
                                  voice: event.reminder_voice,
                                  sms: event.reminder_sms,
                                  confirmSms: event.moi_confirmation_sms,
                                  reminder_call_date: event.reminder_call_date,
                                  reminder_voice_id: event.reminder_voice_id,
                                  reminder_sms_date: event.reminder_sms_date,
                                  event_id: event.event_id,
                                  customer_id: event.customer_id,
                                  event_date: event.event_date,
                                })
                              }
                            >
                              <FaBell />
                            </ListActionBtn>
                          )}

                          {/* Make Payment */}
                          {!isPaid && !isLocked && (
                            <ListActionBtn
                              color="green"
                              title="Make Payment"
                              onClick={() => triggerPayment(event.event_id)}
                            >
                              <FaCreditCard />
                            </ListActionBtn>
                          )}

                           {/* Download Overall Report */}
                           {!isLocked && (
                             <ListActionBtn
                               color="indigo"
                               title="Download Overall Report"
                               onClick={() =>
                                 Swal.fire({
                                   title: "Download Print Report",
                                   text: "Do you want to download the overall print report?",
                                   icon: "question",
                                   showCancelButton: true,
                                   confirmButtonText: "Download",
                                   confirmButtonColor: "#0d9488",
                                   cancelButtonText: "Cancel",
                                   preConfirm: () => handleDownloadOverallReport(
                                       event.event_id,
                                       dispatch,
                                     ),
                                   showLoaderOnConfirm: true,
                                 })
                               }
                             >
                               <FaDownload />
                             </ListActionBtn>
                           )}

                           {/* Close Event */}
                           {!isLocked && (
                             <ListActionBtn
                               color="blue"
                               title="Close Event"
                               onClick={() =>
                                 Swal.fire({
                                   title: "Close Event",
                                   text: "Closing this event will move it to Closed Events. Do you want to continue?",
                                   icon: "warning",
                                   showCancelButton: true,
                                   confirmButtonText: "Yes, Close Event",
                                   confirmButtonColor: "#dc2626",
                                   cancelButtonText: "Cancel",
                                 }).then(async (result) => {
                                   if (result.isConfirmed) {
                                     const success = await handleCloseEvent(
                                       event.event_id,
                                       dispatch,
                                     );
                                     if (success) getEventList();
                                   }
                                 })
                               }
                             >
                               <FaLock />
                             </ListActionBtn>
                           )}

                          {/* Delete */}
                          {!isLocked && (
                            <ListActionBtn
                              color="red"
                              title="Delete Event"
                              onClick={() =>
                                Swal.fire({
                                  title:
                                    "Are you sure you want to delete this event?",
                                  showCancelButton: true,
                                  confirmButtonText: "Confirm Deletion",
                                  confirmButtonColor: "red",
                                }).then((res) => {
                                  if (res.isConfirmed)
                                    deleteEventHandler(event.event_id);
                                })
                              }
                            >
                              <FaTrash />
                            </ListActionBtn>
                          )}
                        </div>
                      </div>

                      {/* ── Row 2: info grid ─────────────────────────────── */}
                      <div className="mt-2.5 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 lg:grid-cols-6">
                                                {/* Total Moi Amount */}
                        <div className="flex items-start gap-1.5 text-xs text-gray-600">
                          <span className="mt-0.5 text-gray-400 shrink-0 text-[10px] font-black uppercase tracking-wide">
                            Total Moi
                          </span>
                          <span className="font-semibold leading-snug text-gray-800">
                            {event.total_moi_amount != null ? `₹ ${event.total_moi_amount}` : "—"}
                          </span>
                        </div>

                        {/* Event name / type */}
                        <div className="flex items-start gap-1.5 text-xs text-gray-600">
                          <span className="mt-0.5 text-gray-400 shrink-0 text-[10px] font-black uppercase tracking-wide">
                            Event
                          </span>
                          <span className="font-semibold leading-snug text-gray-800 truncate">
                            {event.event_name || event.event_type || "—"}
                          </span>
                        </div>

                        {/* Customer name */}
                        <div className="flex items-start gap-1.5 text-xs text-gray-600">
                          <FaPhoneAlt className="mt-0.5 text-gray-400 shrink-0" />
                          <span className="font-medium leading-snug truncate">
                            {event.customer_name
                              ? `${event.customer_name}${event.customer_mobile ? ` · ${event.customer_mobile}` : ""}`
                              : event.customer_mobile || "—"}
                          </span>
                        </div>

                        <div className="flex items-start gap-1.5 text-xs text-gray-600">
                          <FaUser className="mt-0.5 text-gray-400 shrink-0" />
                          <span className="font-medium leading-snug truncate">
                            {[event.eventor_1, event.eventor_2].filter(Boolean).join(", ") || "--"}
                          </span>
                        </div>

                        {/* Date */}
                        <div className="flex items-start gap-1.5 text-xs text-gray-600">
                          <FaCalendarAlt className="mt-0.5 text-gray-400 shrink-0" />
                          <span className="font-medium leading-snug">
                            {event.event_date
                              ? formatCustomDateWithTime(event.event_date)
                              : "—"}
                          </span>
                        </div>

                        <div className="flex items-start gap-1.5 text-xs text-gray-600">
                          <FaUser className="mt-0.5 text-gray-400 shrink-0" />
                          <span className="font-medium leading-snug">
                            {createdOn
                              ? `Created On: ${formatCustomDateWithTime(createdOn)}`
                              : "Created On: --"}
                          </span>
                        </div>

                        {/* Location */}
                        <div className="flex items-start gap-1.5 text-xs text-gray-600">
                          <FaMapMarkerAlt className="mt-0.5 text-gray-400 shrink-0" />
                          <span className="font-medium leading-snug truncate">
                            {location}
                          </span>
                        </div>

                        {/* Operator */}
                        <div className="flex items-start gap-1.5 text-xs">
                          <FaUser className="mt-0.5 text-gray-400 shrink-0" />
                          <span
                            className={`font-medium leading-snug ${noOperator ? "text-red-500" : "text-gray-600"}`}
                          >
                            {event?.operators?.[0]?.operator_name ||
                              "Not assigned"}
                          </span>
                        </div>

                        {/* Services + token count */}
                        <div className="flex items-center gap-1 flex-wrap">
                          <ServicePill
                            active={event.reminder_sms}
                            label="SMS"
                          />
                          <ServicePill
                            active={event.reminder_voice}
                            label="Call"
                          />
                          <ServicePill
                            active={event.moi_confirmation_sms}
                            label="Confirm"
                          />
                          {isPaid && (
                            <span className="text-[10px] font-bold text-blue-600 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-full">
                              {event.token_count} tokens
                            </span>
                          )}
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        <p className="text-center text-xl font-semibold text-teal-600">
          No Events to Show
        </p>
      )}

      {pagination.total > 0 && (
        <div className="mt-6 flex flex-col gap-3 rounded-xl border border-white/70 bg-white/90 p-4 shadow-xl shadow-slate-200/60 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-bold text-slate-900">
              Page {pagination.page} of {Math.max(pagination.total_pages || 1, 1)}
            </p>
            <p className="text-xs text-slate-500">
              Showing {Math.min((pagination.page - 1) * pagination.per_page + 1, pagination.total)}-
              {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} events
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => setPage((current) => Math.max(current - 1, 1))}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <FaChevronLeft />
              Previous
            </button>
            <button
              type="button"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() =>
                setPage((current) =>
                  Math.min(current + 1, pagination.total_pages || current + 1),
                )
              }
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
              <FaChevronRight />
            </button>
          </div>
        </div>
      )}

      {/* Modals (unchanged) */}
      <HandleEventOperatorModal
        isOpen={addOperatorEventId}
        onClose={() => setAddOperatorEventId(null)}
        onSubmit={async (values) => {
          values["event_id"] = addOperatorEventId;
          const res = await HandleAddOperator(values);
          if (res) {
            SuccessToast("Operator Added successfully");
            getEventList();
          }
        }}
      />

      {selectedEvent && (
        <EventDetailsModal
          eventDetails={selectedEvent}
          onClose={handleCloseModal}
          onSave={handleSave}
          isEditable={isEditable}
          refreshEvent={getEventList}
        />
      )}

      <CustomModal
        showModal={selectedOperatorTotals !== null}
        type="Operator Totals"
        bodycontent={
          <div className="mt-4 max-h-[60vh] overflow-y-auto pr-2">
            {selectedOperatorTotals && selectedOperatorTotals.length > 0 ? (
              <div className="space-y-3">
                {selectedOperatorTotals.map((op, idx) => (
                  <div key={idx} className="bg-purple-50/50 border border-purple-100 rounded-xl p-4 flex items-center justify-between shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center border border-purple-200">
                        <FaUser className="text-purple-600 text-lg" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 text-sm">{op.name || "Unknown Operator"}</h4>
                        <span className="text-xs text-purple-600 font-semibold bg-purple-100/50 px-2 py-0.5 rounded-full mt-1 inline-block">
                          {op.entry_count} entries
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500 font-semibold mb-0.5 uppercase tracking-wide">Total Amount</p>
                      <p className="text-lg font-black text-gray-900">₹ {op.total_amount}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <FaUser className="text-gray-300 text-2xl" />
                </div>
                <p className="text-gray-500 font-medium">No operator totals available for this event.</p>
              </div>
            )}
          </div>
        }
        closeModal={() => setSelectedOperatorTotals(null)}
      />
      <CustomModal
        showModal={eventCreds.length > 0}
        type="Event Credentials"
        bodycontent={
          <div className="mt-3 space-y-1">
            {eventCreds.map((items) => (
              <CredentialCard
                key={items.operator_id}
                items={items}
                onDelete={() =>
                  Swal.fire({
                    title: "Are you sure you want to delete this operator?",
                    showCancelButton: true,
                    confirmButtonText: "Confirm Deletion",
                    confirmButtonColor: "red",
                  }).then(async (result) => {
                    if (result.isConfirmed)
                      handleDeleteOperatorLocal(items.operator_id);
                  })
                }
                onCopyAll={async () => {
                  const copied = await handleCopy(
                    `Username: ${items.username}\nPassword: ${items.password}\nResponsible person: ${items.operator_name}-${items.operator_mobile}\n\nYou are solely responsible for your credentials. Please do not share them with anyone.\nநீங்கள் உங்கள் உள்நுழைவு விவரங்களுக்கு முழுப் பொறுப்பும் வகிக்கின்றீர்கள். தயவுசெய்து இதை யாருடனும் பகிர வேண்டாம்.`,
                  );
                  if (copied)
                    SuccessToast(
                      "Credentials copied. Please share to the respective person",
                    );
                }}
              />
            ))}
          </div>
        }
        closeModal={() => setEventCreds([])}
      />
      <EventAddServiceModal
        eventDetails={showAddService}
        setIsOpen={setShowAddService}
        confirmSubmission={(value, resetForm) =>
          handleAddservice(value, resetForm)
        }
      />
    </div>
  );
};

export default OperatorAllOpenEvents;
